/**
 * **A completion ring with its figure in the middle** — the coaching goal, and the 30-day
 * attendance percentage.
 *
 * ── Why bklit rather than a `conic-gradient` ──────────────────────────────────────────────────
 * KANON draws it as a conic gradient over an `@property --ring` percentage, which is a real
 * technique and would work. bklit is used instead because the owner's instruction for this round
 * is that anything chart-shaped comes from the chart library, and because the arc it draws has a
 * rounded cap and a real geometry the gradient cannot express — a 3% goal is a visible tick rather
 * than a hairline nobody can see.
 *
 * ── Three things about `RingChart` that cost time if forgotten ────────────────────────────────
 *   · **It reserves 8px of padding before scaling.** A 62px ring therefore needs `size={78}` and a
 *     62px box around it. `SIZE_PADDING` below is that number, applied once.
 *   · **`Ring` takes its colour as a PROP.** No ancestor class reaches it; `currentColor` does not
 *     work.
 *   · **The unfilled track is painted `var(--border)`,** which on a KANON card is a hairline nobody
 *     can see, so the ring reads as an arc floating in nothing. The wrapper re-points that one
 *     token at `--bg-sunken`, which is the track KANON actually draws.
 *
 * ── The pointer gets the same sentence the screen reader gets ─────────────────────────────────
 * Owner, 1 September 2026: "czemu najeżdżając na krąg żadna wartość się nie pojawia?" The arc used
 * to carry its reading only in an `aria-label`, so a sighted trainer hovering it got nothing. The
 * name is now also the pointer's readout — one string, so the two cannot drift, and the same
 * mechanism `shared/ui/TrainingWeekStrip` answers a hovered day with. The figure in the middle and
 * the denominator the caller prints under the ring already say it without a hover; this is what
 * makes the ring itself answer when the pointer lands on it.
 *
 * ── `RingCenter` is bklit's and is NOT used ───────────────────────────────────────────────────
 * It renders the figure through `@number-flow/react`, a custom element that injects a `<style>`
 * into its shadow root — refused by `style-src-elem 'self'` in a production build, silently,
 * leaving the number unstyled. The centre label is ours, which is the same trade this app takes
 * with Base UI everywhere else.
 */

import type { CSSProperties } from 'react';

import { SCREEN_EASE } from '~/shared/design-system/motion';
import { RingChart } from '~/shared/vendor/bklit/ring-chart';
import { Ring } from '~/shared/vendor/bklit/ring';

import styles from './ReadoutRing.module.css';

/** The figure's type per ring size; the goal's is the base `.figure`. */
const FIGURE_SCALE: Readonly<Record<'goal' | 'wide' | 'key' | 'small', string | undefined>> = {
  goal: undefined,
  wide: styles.figureWide,
  key: styles.figureKey,
  small: styles.figureSmall,
};

/** What `RingChart` reserves around the arc before it scales. Measured, not documented by bklit. */
const SIZE_PADDING = 16;

interface ReadoutRingProps {
  /**
   * The arc's fill. Clamped to 0–100, because an arc past a full turn laps its start and reads as a
   * small number. An overshoot is not lost by the clamp: `figure` prints the real percentage in the
   * middle of the ring, so a closed arc under "113%" states the excess rather than hiding it.
   */
  readonly percent: number;
  /** The ring's outer diameter in pixels — 62 on the goal card, 74 on the Frekwencja card. */
  readonly diameter: number;
  /** The band's thickness in pixels. */
  readonly thickness: number;
  /** The arc's ink, as a token reference. */
  readonly colour: string;
  /** What stands in the hole. Already formatted — this draws it, it does not decide it. */
  readonly figure: string;
  /**
   * The whole ring's meaning in words — its value and its denominator in a sentence. It is the
   * ring's accessible name AND the readout the pointer gets, so a hovering trainer and a screen
   * reader are answered with the same string.
   */
  readonly label: string;
  /** The viewer asked for reduced motion. Required — see `shared/design-system/motion`. */
  readonly reducedMotion: boolean;
  /** The handoff's `dcRing`: 900ms on the goal, 950ms on the wide attendance card. */
  readonly durationMs?: number;
  /**
   * Which of the handoff's two ring sizes this is. It picks the figure's type, which the two
   * cards set differently (15px in the goal's 62px ring, 17px in the 74px one) — a size the
   * component cannot derive from the diameter without inventing a ratio the handoff does not have.
   * `small` is the Pulpit card's 56px ring, where 15px of "100%" touched the stroke (owner, 25
   * September 2026).
   */
  readonly scale?: 'goal' | 'wide' | 'key' | 'small';
}

/**
 * The two optional props take their defaults in the BODY, not in the parameter list: a destructured
 * default makes `babel-plugin-react-compiler@1.0.0` skip the whole component without saying so —
 * `@babel/types` 8 removed `AssignmentPattern` from the `LVal` alias the plugin gates on. See
 * `ReadoutChart`, which carries the measurement.
 */
export function ReadoutRing({
  percent,
  diameter,
  thickness,
  colour,
  figure,
  label,
  reducedMotion,
  durationMs: durationMsProp,
  scale: scaleProp,
}: ReadoutRingProps) {
  const durationMs = durationMsProp ?? 900;
  const scale = scaleProp ?? 'goal';
  const value = Math.min(100, Math.max(0, percent));
  const outerRadius = diameter / 2;

  return (
    <div
      className={styles.wrap}
      style={{ inlineSize: `${String(diameter)}px`, blockSize: `${String(diameter)}px` }}
      role="img"
      aria-label={label}
      title={label}
    >
      {/* Ours, so the overhang rule never has to out-specify a Tailwind class bklit puts on its own
        * box. Half the padding on every side is what centres a chart that is `SIZE_PADDING` wider
        * than the ring it draws. */}
      <span
        className={styles.chart}
        style={{ '--ring-inset': `${String(-SIZE_PADDING / 2)}px` } as CSSProperties}
      >
        <RingChart
          data={[{ label, value, maxValue: 100 }]}
          size={diameter + SIZE_PADDING}
          strokeWidth={thickness}
          baseInnerRadius={outerRadius - thickness}
          /* `enterTransition`, NOT `animationDuration`. bklit declares `animationDuration` on its
           * props and then never destructures it (`ring-chart.tsx` reads `enterTransition` and
           * nothing else), so passing the duration there is a no-op: every ring in the app entered
           * on the library's 1100ms default while this file's own comment promised the handoff's
           * 900. Measured 2 September 2026, after the roster panel — which passed `enterTransition`
           * by hand — was folded into this wrapper and its entrance visibly changed.
           *
           * The curve is `SCREEN_EASE`, the same one every other entrance in the app runs on, so a
           * ring filling beside a card that rises reads as one gesture rather than two. */
          enterTransition={{ duration: durationMs / 1000, ease: [...SCREEN_EASE] }}
        >
          <Ring index={0} color={colour} animate={!reducedMotion} />
        </RingChart>
      </span>
      <span
        className={`${String(styles.figure)} ${FIGURE_SCALE[scale] ?? ''}`}
        aria-hidden="true"
      >
        {figure}
      </span>
    </div>
  );
}
